<template>
    <div>
        <b-modal v-model="isOpen" title="Address Information" size="lg" hide-footer @hidden="closeDialog" @hide="closeDialog">
            <ValidationObserver v-slot="{ handleSubmit }" ref="observer">
                <form @submit.prevent="handleSubmit(onSubmit)" class="mt-4">
                    <div class="row">
                        <template v-if="addressInfo && addressInfo.length > 0">          
                            <div class="table-responsive mb-4">
                                <table class="table table-bordered">
                                    <thead>
                                        <tr>
                                            <th>Type</th>
                                            <th>Address</th>
                                            <th>City</th>
                                            <th>State</th>
                                            <th>ZIP</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="address in addressInfo" :key="address.address_id">
                                            <td>{{ getType(address.type) }}</td>
                                            <td>
                                                {{ address.address1 }}
                                                <br v-if="address.address2">
                                                <small v-if="address.address2">{{ address.address2 }}</small>
                                            </td>
                                            <td>{{ address.city }}</td>
                                            <td>{{ address.state }}</td>
                                            <td>{{ address.zip }}</td>
                                            <td>
                                                <b-button class="custom-update-btn p-2" size="sm"
                                                    v-if="address.is_primary == '0'"
                                                    @click="setPrimaryAddress(address)">
                                                    Make Primary
                                                </b-button>
                                                <b-button class="pri-btn-success" size="sm" v-else>
                                                    <i class="far fa-check-circle"></i>
                                                    Primary
                                                </b-button>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </template>

                        <div class="col-md-6">
                            <div class="form-group">
                                <label>Street Address <span class="text-danger">*</span></label>
                                <ValidationProvider v-slot="{ errors }" name="Street Address" rules="required">
                                    <div>
                                        <b-form-input v-model="formData.address1" placeholder="Enter Street Address" />
                                        <span class="text-danger">{{ errors[0] }}</span>
                                    </div>
                                </ValidationProvider>
                            </div>
                        </div>
                        <div class="col-md-6">
                            <div class="form-group">
                                <label>Apt/Suite/Other</label>
                                <b-form-input v-model="formData.address2" placeholder="Enter Apt/Suite/Other" />
                            </div>
                        </div>
                    </div>

                    <div class="row mt-3">
                        <div class="col-md-6">
                            <div class="form-group">
                                <label>City <span class="text-danger">*</span></label>
                                <ValidationProvider v-slot="{ errors }" name="City" rules="required">
                                    <div>
                                        <b-form-input v-model="formData.city" placeholder="Enter City" />
                                        <span class="text-danger">{{ errors[0] }}</span>
                                    </div>
                                </ValidationProvider>
                            </div>
                        </div>
                        <div class="col-md-6">
                            <div class="form-group">
                                <label>State <span class="text-danger">*</span></label>
                                <b-form-select v-model="formData.state" :options="stateOptions" required />
                            </div>
                        </div>
                    </div>

                    <div class="row mt-3">
                        <div class="col-md-6">
                            <div class="form-group">
                                <label>ZIP Code <span class="text-danger">*</span></label>
                                <ValidationProvider v-slot="{ errors }" name="ZIP Code" rules="required|numeric|min:5">
                                    <div>
                                        <b-form-input v-model="formData.zip" placeholder="Enter ZIP Code" type="text" />
                                        <span class="text-danger">{{ errors[0] }}</span>
                                    </div>
                                </ValidationProvider>
                            </div>
                        </div>
                        <div class="col-md-6">
                            <label>Type <span class="text-danger">*</span></label>
                            <ValidationProvider v-slot="{ errors }" name="Type" rules="required">
                                <b-form-select v-model="formData.type" :options="addressTypes" />
                                <div>
                                    <span class="text-danger">{{ errors[0] }}</span>
                                </div>
                            </ValidationProvider>
                        </div> 
                    </div>

                    <div class="form-group mt-3">
                        <b-form-checkbox v-model="formData.bypass" value="1" class="checkbox-default" unchecked-value="0">
                            <span class="p-2">Continue without Address Validation</span>
                        </b-form-checkbox>
                    </div>

                    <div v-if="loading" class="loading-spinner">
                        <div class="spinner"></div>
                    </div>

                    <div class="d-flex justify-content-end border-0 mt-3">
                        <b-button class="can-btn-secondary" @click="closeDialog" style="margin-right: 4px;">
                            Cancel
                        </b-button>
                        <b-button type="submit" class="custom-update-btn m-0" :disabled="loading">
                            Submit
                        </b-button>
                    </div>                   
                </form>
            </ValidationObserver>
        </b-modal>

        <!-- Confirmation Modal -->
        <b-modal id="confirm-primary-modal" ok-title="Yes" cancel-title="No" centered  hide-footer
            @ok="handleConfirmPrimary" ref="confirmPrimaryModal" hide-header-close>
            <template #modal-title>
                <span>Change Primary Address</span>
            </template>
            <div>
                <p>Are you sure you want to make this the primary address?</p>
                <div class="border p-2 my-3 bg-light">
                    <strong>Selected Address:</strong><br>
                    <span v-if="selectedAddress">
                        {{ selectedAddress.address1 }}
                        <template v-if="selectedAddress.address2">, {{ selectedAddress.address2 }}</template>
                        , {{ selectedAddress.city }}, {{ selectedAddress.state }} {{ selectedAddress.zip }}
                    </span>
                </div>
                <p>Please note that if your plan(s) are not available in the state for this address, your plan(s) will be terminated.</p>
                <div class="d-flex justify-content-end border-0 mt-3">
                    <button @click="closeConfirmationBox" style="margin-right: 4px;" class="btn can-btn-secondary" >Close</button>
                    <button @click="handleConfirmPrimary" class="btn  custom-update-btn">Yes</button>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import axios from 'axios';
import { states,capitalize } from '../../../utils/constant';

export default {
    name: 'AddressInformationModal',
    components: {
        ValidationProvider,
        ValidationObserver
    },
    props: {
        isOpen: {
            type: Boolean,
            required: true
        },
        addressInfo: {
            type: Array,
            default: () => []
        },
        memberData: {
            type: Object,
            required: true
        },
        policyInfo: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            loading: false,
            formData: {
                address1: '',
                address2: '',
                city: '',
                state: '',
                zip: '',
                bypass: "0",
                type: 'R',
                memberid: null,
                policy_id: null
            },
            stateOptions: [...states],
            addressTypes: [
                { value: 'R', text: 'Residential' },
                { value: 'B', text: 'Billing' },
                { value: 'M', text: 'Mailing' }
            ],
            selectedAddress: null
        }
    },
    watch: {
        isOpen(val) {
            console.log("Is opening")
            if (val && this.memberData && this.policyInfo) {
                console.log("handling data",this.memberData);
                console.log("handling policy",this.policyInfo);
                this.formData.memberid = this.memberData.user_id;
                // If you have policy_id in memberData, set it here
                 this.formData.policy_id = this.policyInfo.policy_id;
                 
            }
        }
    },
    methods: {
        getType(type) {
            return this.addressTypes.find(el => el.value === type)?.text || '';
        },
        validateUsps() {
            this.loading = true;
            const url = process.env.VUE_APP_API_URL_V2 + "validate-address-usps";
            const payload = {
                address1: this.formData.address1,
                address2: this.formData.address2,
                city: this.formData.city,
                state: this.formData.state,
                zip: this.formData.zip
            };

            return axios.post(url, payload)
                .then(response => {
                    return response.data;
                })
                .catch(error => {
                    const errors = error.response?.data?.data || [];
                    errors.forEach(element => {
                        this.$notify(capitalize(element.error_message), 'error');
                    });
                    throw error;
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        async saveAddress() {
            this.loading = true;
            const url = process.env.VUE_APP_API_URL_V2 + "add-member-address-extra-health";

            console.log(this.memberData)
            console.log(this.policyInfo)

            this.formData.memberid =  this.memberData.user_id
            this.formData.policy_id =  this.policyInfo.policy_id
            
            try {
                if (!this.formData.memberid || !this.formData.policy_id) {
                    throw new Error('Member ID or Policy ID is missing');
                }
                const response = await axios.post(url, {
                    ...this.formData,
                    bypass:this.formData.bypass === '1'
                });
                if (response.data.status == 'success') {
                    this.closeDialog();
                     this.$notify("Address saved successfully");
                    this.$emit('reloadInfo');
                    
                } else {
                    throw new Error(response.data.message || "Failed to save address");
                }
            } catch (error) {
                console.error("Address save error:", error.response?.data);
                this.$notify(error.message, "error");
                throw error;                
            } finally {
                this.loading = false;
            }
        },
        async onSubmit() {
            try {
                if (this.formData.bypass === '0') {
                    await this.validateUsps();
                }
                console.log("handling data",this.formData);
                await this.saveAddress();
            } catch (error) {
                
                if (this.formData.bypass === '1') {
            await this.saveAddress();
        } else {
            console.error("Address validation failed:", error);
        }
            }
        },
        closeDialog() {
            this.$emit('update:isOpen', false);
            this.$emit('close');
            this.formData = {
                address1: '',
                address2: '',
                city: '',
                state: '',
                zip: '',
                bypass: "0",
                type: 'R',
                memberid: null,
                policy_id: null
            };
        },
        setPrimaryAddress(address) {
            this.selectedAddress = address;
            this.$refs.confirmPrimaryModal.show();
        },
        closeConfirmationBox(){
            this.$refs.confirmPrimaryModal.hide();
        },
        async handleConfirmPrimary(bvModalEvent) {
            bvModalEvent.preventDefault();
            if (!this.selectedAddress) return;

            this.loading = true;
            const url = process.env.VUE_APP_API_URL_V2 + "set-extra-health-primary-address";
            const payload = {
                address_id: this.selectedAddress.address_id,
                memberid: this.memberData.user_id,
                type: this.selectedAddress.type,
                confirmed: true
            };
            this.formData.memberid =  this.memberData.user_id
            try {
                const response = await axios.post(url, payload);
                this.closeDialog();
                this.$notify(response.data.message || "Primary address updated successfully");
                this.$emit('reloadInfo');
                
            } catch (error) {
                const errorMsg = error.response?.data?.message || "Failed to update primary address";
                this.$notify(errorMsg, "error");
            } finally {
                this.loading = false;
                this.$refs.confirmPrimaryModal.hide();
            }
        }
    }
}
</script>

<style scoped>
.loading-spinner {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.7);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}

.spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #3498db;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
}

.modal-body {
    color: red;
}

.primary-address-modal .modal-title {
    color: white !important;
}

.white-header .modal-title {
    color: white !important;
}

.white-header {
    color: white !important;
}
.custom-update-btn {
  background-color: #1392b8 !important; /* or any color you want */
  color: white;
  border: none;
}

.custom-update-btn:hover {
  background-color: #1392b8;
}

.can-btn-secondary{
  background-color: #6c757d !important; /* or any color you want */
  color: white;
  border: none;
}
.can-btn-secondary:hover {
    background-color: #6c757d;
}
.pri-btn-success{
  background-color: #198754 !important; /* or any color you want */
  color: white;
  border: none;
}
.pri-btm-success:hover{
    background-color: #198754;
}

</style>